<script setup>
import { ref, computed, onMounted } from 'vue'
import { useStore } from 'vuex'
import { API, HOST } from '@/utils/http'
import { firebase } from '@/router'
import { Zap } from 'lucide-vue-next'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import PlanCard from '@/components/PlanCard.vue'
import { useFeatures } from '@/composables/useFeatures'

const isOpen = ref(false)
const plans = ref({})
const loading = ref(false)
const store = useStore()
const { showPaymentLinks } = useFeatures()

const currentOrg = computed(() => store.getters['org/getSelectedOrg'])
const currentPlan = computed(() => {
  if (!currentOrg.value) return 'free'

  const tierMapping = {
    0: 'free',
    1: 'basic',
    2: 'unlimited'
  }

  const planValue = currentOrg.value.plan !== undefined ? currentOrg.value.plan : 0
  return tierMapping[planValue] || 'free'
})

const sortedPlans = computed(() => {
  const planArray = Object.entries(plans.value).map(([key, plan]) => ({
    key,
    ...plan
  }))

  return planArray.sort((a, b) => a.plan - b.plan)
})

const fetchPlans = async () => {
  try {
    loading.value = true

    const response = await API({
      method: 'GET',
      url: `${HOST}/organization/${currentOrg.value.organization_id}/plans`
    })

    plans.value = response.data
  } catch (error) {
    console.error('Error fetching plans:', error)
  } finally {
    loading.value = false
  }
}

const open = () => {
  isOpen.value = true
  fetchPlans()
}

const isPlanCurrent = (planKey) => {
  return planKey === currentPlan.value
}

const handleUpgrade = async (planKey) => {
  if (!showPaymentLinks.value) {
    console.log('Payment links are disabled in this environment')
    return
  }
  
  try {
    loading.value = true
    const response = await API({
      method: 'POST',
      url: `${HOST}/organization/${currentOrg.value.organization_id}/create-payment-link`,
      data: {
        plan_name: planKey
      }
    })

    if (response.data && response.data.payment_link) {
      // Redirect user to Stripe payment link
      window.location.href = response.data.payment_link
    }
  } catch (error) {
    console.error('Error creating payment link:', error)
    // You might want to show an error message to the user here
  } finally {
    loading.value = false
  }
}

defineExpose({
  open
})
</script>

<template>
  <Dialog :open="isOpen" @update:open="isOpen = $event">
    <DialogTrigger as-child>
      <slot>
        <Button variant="outline">Select Plan</Button>
      </slot>
    </DialogTrigger>
    <DialogContent class="max-w-4xl max-h-[90vh] overflow-y-auto">
      <DialogHeader>
        <DialogTitle class="flex items-center gap-2">
          <Zap class="h-5 w-5" />
          Choose Your Plan
        </DialogTitle>
        <DialogDescription>
          Select the plan that best fits your organization's needs.
        </DialogDescription>
      </DialogHeader>

      <div v-if="loading" class="flex justify-center py-8">
        <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>

      <div v-else class="grid grid-cols-1 md:grid-cols-3 gap-4 py-4">
        <PlanCard
          v-for="plan in sortedPlans"
          :key="plan.key"
          :plan="plan"
          :is-current-plan="isPlanCurrent(plan.key)"
          :show-upgrade-button="showPaymentLinks"
          @upgrade="handleUpgrade"
        />
      </div>
    </DialogContent>
  </Dialog>
</template>
